/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description helper function to save messaging user info
 */

import type { User } from '@zcode/bizos'
import { USER_ID_LOCAL_STORAGE_KEY } from '@zcode/integrations'
import { localStorageSingleton } from '@zcode/nr-utils'
import type { Listable, Profile, Property } from '@zcode/state-type'
import type { Dispatch } from '../../../types'
import Marketing from '../../../utils/marketing'
import { parseFullName } from '../../../utils/name'
import saveUserInfo from '../../saveUserInfo'
import saveMessagedToLocalStorage from './saveMessagedToLocalStorage'

interface MessageData {
  completionEvtName?:
    | 'MessageSimilar'
    | 'MessageSimilarMobile'
    | 'Schedule A Tour Modal'
    | 'Message Modal'
    | 'MessageInline'
    | 'Schedule A Tour MessageInline'
    | 'One Tap'
    | 'Schedule A Tour One Tap'
  email?: string | null
  feedNames?: ReadonlyArray<string | null | undefined>
  ignoreEqualCTAOrigin?: boolean
  messagedBuildingIds?: readonly number[]
  messagedListingIds?: readonly number[]
  messageModified?: boolean
  moveIn?: string | null
  msg?: string
  name?: string
  origin?: string
  phone?: string | null
  /**
   * When the renter would like to tour, in "EEEE, LLL d 'at' h:mm aaa" format
   */
  selectedTimes?: readonly string[]
  /**
   * When the renter would like to tour, in "yyyy-MM-dd'T'kk:mm':00.000" format
   */
  selectedTimesISO8601?: readonly string[]
}

interface Props {
  config: {
    entity: Listable | Property
    isLoggedIn: boolean
    isMulti?: boolean
  }
  equalCTAIntent?: 'Apply' | 'Message' | 'Tour' | 'Waitlist' | undefined
  messageData: MessageData
  moveInText: 'ASAP' | 'In the next 30 days' | 'Later this year' | undefined
  setProfile: (profile: Profile) => void
  timestamp: number
  user: User | null
}

const saveMessageUser =
  ({ config, equalCTAIntent, messageData, moveInText, setProfile, timestamp, user }: Props) =>
  (dispatch: Dispatch) => {
    const { entity, isMulti, isLoggedIn } = config
    const { email, messagedBuildingIds, messagedListingIds, moveIn, name, phone } = messageData

    const noRetargetLead = !isMulti && entity && entity.isRentpath
    const { firstName, lastName } = parseFullName(name)
    const traktorUserId = localStorageSingleton.get(USER_ID_LOCAL_STORAGE_KEY)

    dispatch(
      saveUserInfo(
        {
          email,
          firstName,
          lastName,
          moveIn,
          moveInText,
          name,
          noRetargetLead,
          phone,
          user: isLoggedIn ? user : undefined,
        },
        setProfile
      )
    )

    if (!isLoggedIn) {
      if (!noRetargetLead) {
        // @ts-expect-error: missing properties joinedAt, ...
        Marketing.trackIdentify({
          email,
          firstName,
          lastName,
          moveIn,
          phone,
          traktorUserId,
        })
      }

      saveMessagedToLocalStorage({
        equalCTAIntent,
        messagedBuildingIds,
        messagedListingIds,
        timestamp,
      })
    }
  }

export default saveMessageUser
